import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { absUrl } from "@/lib/helpers";
import ImageUpload from "@/components/admin/ImageUpload";

export default function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", order: 0, active: true, image_url: "", color: "", text_color: "", text_size: "base", require_type: true });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/categories", { params: { all: true } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", order: items.length, active: true, image_url: "", color: "", text_color: "", text_size: "base", require_type: true }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, order: c.order, active: c.active, image_url: c.image_url || "", color: c.color || "", text_color: c.text_color || "", text_size: c.text_size || "base", require_type: c.require_type !== false }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama kategori wajib diisi");
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Kategori disimpan");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/categories/${toDelete.id}`);
      toast.success("Kategori dihapus"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
    finally { setToDelete(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Kategori</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kelola kategori produk.</p>
        </div>
        <button data-testid="add-category-btn" onClick={openNew} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Tambah</button>
      </div>

      <div className="mt-6 space-y-2">
        {items.map((c) => (
          <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
            <div className="flex items-center gap-3">
              {c.image_url && <img src={absUrl(c.image_url)} alt={c.name} className="h-9 w-9 rounded-lg object-cover" />}
              <span className="font-medium">{c.name}</span>
              {!c.active && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">Nonaktif</span>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(c)} className="rounded-lg p-2 hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-category-${c.id}`} onClick={() => setToDelete(c)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Kategori</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Kategori</Label><Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <ImageUpload label="Ikon / Gambar Kategori (opsional)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="category-image-upload" />
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div>
                <Label>Butuh pilih tipe HP</Label>
                <p className="text-xs text-muted-foreground">Aktif: muncul sub-kategori tipe HP. Nonaktif: klik langsung tampilkan produk.</p>
              </div>
              <Switch data-testid="category-require-type" checked={form.require_type} onCheckedChange={(v) => setForm({ ...form, require_type: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Warna Kotak</Label>
                <input type="color" data-testid="category-color" value={form.color || "#0F172A"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-border" />
              </div>
              <div>
                <Label className="text-xs">Warna Teks</Label>
                <input type="color" data-testid="category-text-color" value={form.text_color || "#ffffff"} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-border" />
              </div>
              <div>
                <Label className="text-xs">Ukuran Teks</Label>
                <Select value={form.text_size} onValueChange={(v) => setForm({ ...form, text_size: v })}>
                  <SelectTrigger data-testid="category-text-size" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Kecil</SelectItem>
                    <SelectItem value="base">Sedang</SelectItem>
                    <SelectItem value="lg">Besar</SelectItem>
                    <SelectItem value="xl">Sangat Besar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Kosongkan warna untuk memakai tema default. Reset warna dengan tombol di bawah.</p>
            <button type="button" onClick={() => setForm({ ...form, color: "", text_color: "" })} className="text-xs font-medium text-accent hover:underline">Pakai warna default</button>
            <div><Label>Urutan</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="mt-1" /></div>
            <div className="flex items-center justify-between"><Label>Aktif</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
          </div>
          <DialogFooter><button data-testid="save-category-btn" onClick={save} className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus kategori?</AlertDialogTitle><AlertDialogDescription>Kategori "{toDelete?.name}" akan dihapus. Kategori yang masih dipakai produk tidak dapat dihapus.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-category" onClick={confirmDelete} className="bg-destructive">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
